import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Sparkles, X } from 'lucide-react';

const DISMISS_KEY = 'indusfact:banners_dismissed_v1';

/**
 * PromoBanner — sitewide top banner for active automatic promotions.
 * - Fetches /api/promotions/banners on mount
 * - Rotates through banners every 8s if more than one
 * - Each banner can be individually dismissed (localStorage-backed for the session)
 */
export const PromoBanner = () => {
  const [banners, setBanners] = useState([]);
  const [idx, setIdx] = useState(0);
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem(DISMISS_KEY) || '[]')); }
    catch { return new Set(); }
  });

  useEffect(() => {
    let alive = true;
    api.get('/promotions/banners').then(r => {
      if (!alive) return;
      setBanners(Array.isArray(r.data) ? r.data : []);
    }).catch(() => setBanners([]));
    return () => { alive = false; };
  }, []);

  const visible = banners.filter(b => !dismissed.has(b.id));
  useEffect(() => {
    if (visible.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % visible.length), 8000);
    return () => clearInterval(t);
  }, [visible.length]);

  if (!visible.length) return null;
  const current = visible[idx % visible.length] || visible[0];

  const dismiss = (id) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify(Array.from(next))); } catch { /* ignore */ }
  };

  const Wrap = current.banner_link ? Link : 'div';
  const wrapProps = current.banner_link ? { to: current.banner_link } : {};

  return (
    <div
      className='w-full bg-gradient-to-r from-primary to-primary/85 text-primary-foreground'
      data-testid='promo-banner-bar'
    >
      <div className='indus-container flex items-center gap-2 py-2 text-xs sm:text-sm'>
        <Sparkles className='h-4 w-4 shrink-0' />
        <Wrap
          {...wrapProps}
          className={`flex-1 truncate ${current.banner_link ? 'hover:underline' : ''}`}
          data-testid='promo-banner-message'
        >
          {current.banner_text}
        </Wrap>
        {visible.length > 1 && (
          <div className='flex gap-1 shrink-0' aria-label='Banner navigation'>
            {visible.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Show banner ${i + 1}`}
                className={`h-1.5 w-1.5 rounded-full transition-opacity ${i === (idx % visible.length) ? 'bg-primary-foreground' : 'bg-primary-foreground/40'}`}
              />
            ))}
          </div>
        )}
        <button
          type='button'
          onClick={() => dismiss(current.id)}
          className='shrink-0 rounded-md p-1 hover:bg-primary-foreground/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/50'
          aria-label='Dismiss banner'
          data-testid='promo-banner-dismiss'
        >
          <X className='h-3.5 w-3.5' />
        </button>
      </div>
    </div>
  );
};

export default PromoBanner;
